import clsx from 'clsx';
import type { ButtonHTMLAttributes, PropsWithChildren, ReactNode, RefObject } from 'react';

import { Link, type LinkProps } from 'react-router-dom';

import { Loader, type LoaderProps } from '../loader';

import styles from './button.module.scss';

export const Button = (props: PropsWithChildren<ButtonProps | ButtonLinkProps>) => {
  const {
    icon,
    loading,
    active,
    children,
    iconOnly,
    fullWidth,
    className,
    loaderColor,
    color = 'hero',
    size = 'default',
    appearance = 'default',
    cornerRadius = 'rounded',
    itemsDirection = 'start',
    ...rest
  } = props;

  const loaderColorMap: Record<string, LoaderProps['color']> = {
    dark: 'dark',
    success: 'light',
    'success-sc': 'success-sc',
  };

  const shared = {
    className: clsx(
      styles.button,
      styles[`s-${size}`],
      styles[`c-${color}`],
      styles[cornerRadius],
      styles[itemsDirection],
      styles[`a-${appearance}`],
      className,
      {
        [styles.active]: active,
        [styles.iconOnly]: iconOnly,
        [styles.fullWidth]: fullWidth,
        'pointer-events-none': loading,
      }
    ),
    'aria-busy': loading || undefined,
  };

  const content = (
    <>
      {icon}
      {!!children && (
        <span className="ellipsis-text" style={{ position: 'relative' }}>
          {loading ? (
            <>
              <Loader variant="steps" color={loaderColor || loaderColorMap[color] || 'light'} />
              <span style={{ opacity: 0, display: 'block', height: 0, visibility: 'hidden' }}>{children}</span>
            </>
          ) : (
            children
          )}
        </span>
      )}
    </>
  );

  if (rest.to !== undefined) {
    return (
      <Link {...shared} {...rest} onClick={loading ? (event) => event.preventDefault() : rest.onClick}>
        {content}
      </Link>
    );
  }

  return (
    <button {...shared} {...rest} onClick={loading ? undefined : rest.onClick}>
      {content}
    </button>
  );
};

interface ButtonVisualProps {
  icon?: ReactNode;
  active?: boolean;
  loading?: boolean;
  iconOnly?: boolean;
  fullWidth?: boolean;
  itemsDirection?: 'start' | 'end';
  loaderColor?: LoaderProps['color'];
  size?: 'small' | 'medium' | 'big' | 'default';
  cornerRadius?: 'smooth' | 'rounded';
  appearance?: 'default' | 'outline' | 'clear' | 'greyscale' | 'plain';
  color?: 'hero' | 'success' | 'success-sc' | 'light' | 'dark' | 'grey' | 'tertiary' | 'danger';
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>, ButtonVisualProps {
  to?: undefined;
  ref?: RefObject<HTMLButtonElement | null>;
}

interface ButtonLinkProps extends Omit<LinkProps, 'color'>, ButtonVisualProps {
  ref?: RefObject<HTMLAnchorElement | null>;
}
